import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuth } from "@/hooks/use-auth";
import { ROUTES } from "@/lib/constants";
import { LoadingSpinner } from "@/components/shared/loading-spinner";

/**
 * Auth gate. Renders ``children`` once the auth provider has
 * hydrated and a user is present; redirects to ``/login`` with the
 * intended destination preserved in ``location.state.from`` so the
 * login page can return the user to the page they tried to open.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ocean-50">
        <LoadingSpinner size={28} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />
    );
  }

  return <>{children}</>;
}
